import { types, flow, getRoot, getSnapshot } from 'mobx-state-tree'
import _ from 'lodash'

import { API } from '../api'
import { Goal as IncludedGoal } from './Goals'
import { CompanyStock } from './CompanyStock'
import { StockOption } from './StockOptions'

const { model, array, optional, maybeNull, maybe, number, string, boolean } = types

const investmentMixTable = {
  43: { cCash: 30, aCash: 0 },
  29: { cCash: 36, aCash: 5 },
  20: { cCash: 39, aCash: 10 },
  10: { cCash: 44, aCash: 15 },
  7: { cCash: 51, aCash: 21 },
  3: { cCash: 56, aCash: 27 },
  1: { cCash: 67, aCash: 39 },
  0: { cCash: 77, aCash: 46 },
}

const EnginePerson = model({
  ongoingAnnualContributions: maybeNull(number),
  ongoingMonthlyContributions: maybeNull(number),
  postTaxAnnualContribDollars: maybeNull(number),
  postTaxAnnualContributions: maybeNull(number),
  preTaxAnnualContribDollars: maybeNull(number),
  preTaxAnnualContributions: maybeNull(number),
  retAge: maybeNull(number),
  rothAnnualContribDollars: maybeNull(number),
  rothAnnualContributions: maybeNull(number),
  preTaxSSIncome: maybeNull(number),
  preTax457AnnualContributions: maybeNull(number),
  preTax457AnnualContribDollars: maybeNull(number),
  roth457AnnualContributions: maybeNull(number),
  preTax403bAnnualContribDollars: maybeNull(number),
  preTax403bAnnualContributions: maybeNull(number),
})

const EngineCase = model({
  caseId: maybeNull(number),
  currentTotalBalance: maybeNull(number),
  currentTaxRate: maybeNull(number),
  estimatedFutureValue: maybeNull(number),
  estimatedInflationAdjustedValue: maybeNull(number),
  estimatedMonthlyIncomeFromInvestments: maybeNull(number),
  estimatedMonthlySSIncome: maybeNull(number),
  estimatedTotalMonthlyIncome: maybeNull(number),
  estimatedTotalMonthlyIncomeMax: maybeNull(number),
  estimatedTotalMonthlyIncomeMin: maybeNull(number),
  estAftTaxIncomeAtHundredPercent: maybeNull(number),
  name: maybeNull(string),
  otherFinancialGoalsTotal: maybeNull(number),
  outOfPlanMonthlySavings: maybeNull(number),
  primary: maybeNull(EnginePerson),
  projectedMonthlyInflationAdjustedRetirementIncFactor: maybeNull(number),
  projectedMonthlyInflationAdjustedRetirementInc: maybeNull(number),
  projectedMonthlyRetirementInc: maybeNull(number),
  recomMaxRiskLevel: maybeNull(number),
  recomMinRiskLevel: maybeNull(number),
  retirementIncomeGoal: maybeNull(number),
  riskLevel: maybeNull(number),
  riskLevelAll: maybeNull(number),
  spouse: maybeNull(EnginePerson),
  includedGoals: array(IncludedGoal),
  currentAfterTax: maybeNull(number),
  currentAfterTaxIncome: maybeNull(number),
  targetedAftTaxIncome: maybeNull(number),
  companyStocks: maybeNull(array(CompanyStock)),
  stockOptions: maybeNull(array(StockOption)),
  scenarioDescription: maybeNull(string),
  incomeBeforeTax: maybeNull(number),
  taxAmount: maybeNull(number),
  clientContributions: maybeNull(number),
  spouseContributions: maybeNull(number),
}).views(self => ({
  get includedGoalsIds() {
    return self.includedGoals.map(goal => goal.id)
  },
  get estimatedTotalMonthlyIncomeInflationAdjustedMin() {
    return (
      self.estimatedTotalMonthlyIncomeMin *
      ((self.estimatedInflationAdjustedValue + 1) / (self.estimatedFutureValue + 1))
    )
  },
  get estimatedTotalMonthlyIncomeInflationAdjusted() {
    return (
      self.estimatedTotalMonthlyIncome *
      ((self.estimatedInflationAdjustedValue + 1) / (self.estimatedFutureValue + 1))
    )
  },
  get estimatedTotalMonthlyIncomeInflationAdjustedMax() {
    if (self.estimatedFutureValue === 0 || self.estimatedFutureValue === null) {
      return 0
    }
    return (
      self.estimatedTotalMonthlyIncomeMax *
      (self.estimatedInflationAdjustedValue / self.estimatedFutureValue)
    )
  },
  get riskLevelTextShort() {
    if (self.riskLevel === null) {
      return ''
    } else if (self.riskLevel < self.recomMinRiskLevel) {
      return 'Lower than'
    } else if (
      self.riskLevel <= self.recomMaxRiskLevel &&
      self.riskLevel >= self.recomMinRiskLevel
    ) {
      return 'Within'
    } else if (self.riskLevel > self.recomMaxRiskLevel) {
      return 'Higher than'
    } else {
      return ''
    }
  },
  get riskLevelText() {
    if (self.riskLevel === null) {
      return ''
    } else if (self.riskLevel < self.recomMinRiskLevel) {
      return 'Lower than our preferred range'
    } else if (
      self.riskLevel <= self.recomMaxRiskLevel &&
      self.riskLevel >= self.recomMinRiskLevel
    ) {
      return 'Within our preferred range'
    } else if (self.riskLevel > self.recomMaxRiskLevel) {
      return 'Higher than our preferred range'
    } else {
      return ''
    }
  },
  get riskLevelAllText() {
    if (self.riskLevelAll === null) {
      return ''
    } else if (self.riskLevelAll < self.recomMinRiskLevel) {
      return 'Lower than our preferred range'
    } else if (
      self.riskLevelAll <= self.recomMaxRiskLevel &&
      self.riskLevelAll >= self.recomMinRiskLevel
    ) {
      return 'Within our preferred range'
    } else if (self.riskLevelAll > self.recomMaxRiskLevel) {
      return 'Higher than our preferred range'
    } else {
      return ''
    }
  },
  get riskScaleIndex() {
    if (self.recomMaxRiskLevel === null || self.recomMinRiskLevel === null) {
      return -1
    }

    if (self.riskLevel <= self.recomMaxRiskLevel && self.riskLevel >= self.recomMinRiskLevel) {
      const difference = Math.max(self.recomMaxRiskLevel - self.recomMinRiskLevel, 0)
      if (self.riskLevel < self.recomMinRiskLevel + difference / 3) {
        return 0
      } else if (self.riskLevel < self.recomMinRiskLevel + (2 * difference) / 3) {
        return 1
      } else {
        return 2
      }
    } else {
      return -1
    }
  },
  get withinRange() {
    return self.riskScaleIndex >= 0
  },
  get isOnTrack() {
    const { retirementIncomeGoalPct } = getRoot(self).person
    return self.retirementIncomeGoal >= retirementIncomeGoalPct
  },
  get totalAnnualSavings() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 2:
        return this.totalAnnualSavings403b
      case 4:
        return this.totalAnnualSavings457
      case 1:
      case 23:
      default:
        return this.totalAnnualSavings401k401a
    }
  },
  get totalAnnualSavings401k401a() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    return (
      (self?.primary?.preTaxAnnualContribDollars ?? 0) +
      (self?.primary?.postTaxAnnualContribDollars ?? 0) +
      (self?.primary?.rothAnnualContribDollars ?? 0)
    )
  },
  get totalAnnualSavings403b() {
    return self?.primary?.preTax403bAnnualContribDollars ?? 0
  },
  get totalAnnualSavings457() {
    return (
      (self?.primary?.preTax457AnnualContribDollars ?? 0) +
      (self?.primary?.roth457AnnualContribDollars ?? 0)
    )
  },
  get totalAnnualPercentage() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 2:
        return this.totalAnnualPercentage403b
      case 4:
        return this.totalAnnualPercentage457
      default:
        return this.totalAnnualPercentage401k401a
    }
  },
  get totalAnnualPercentage401k401a() {
    return (
      (self?.primary?.preTaxAnnualContributions ?? 0) +
      (self?.primary?.postTaxAnnualContributions ?? 0) +
      (self?.primary?.rothAnnualContributions ?? 0)
    )
  },
  get totalAnnualPercentage403b() {
    return self?.primary?.preTax403bAnnualContributions ?? 0
  },
  get totalAnnualPercentage457() {
    return (
      (self?.primary?.preTax457AnnualContributions ?? 0) +
      (self?.primary?.roth457AnnualContributions ?? 0)
    )
  },
  get totalAnnualSavingsSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    if (!spouseEmployerAccount || !spouseEmployerAccount.planType) return 0

    switch (spouseEmployerAccount.planType) {
      case 2:
        return this.totalAnnualSavingsSpouse403b
      case 4:
        return this.totalAnnualSavingsSpouse457
      default:
        return this.totalAnnualSavingsSpouse401k401a
    }
  },
  get totalAnnualSavingsSpouse401k401a() {
    return (
      (self?.spouse?.preTaxAnnualContribDollars ?? 0) +
      (self?.spouse?.postTaxAnnualContribDollars ?? 0) +
      (self?.spouse?.rothAnnualContribDollars ?? 0)
    )
  },
  get totalAnnualSavingsSpouse403b() {
    return self?.spouse?.preTax403bAnnualContribDollars ?? 0
  },
  get totalAnnualSavingsSpouse457() {
    return (
      (self?.spouse?.preTax457AnnualContribDollars ?? 0) +
      (self?.spouse?.roth457AnnualContribDollars ?? 0)
    )
  },
  get totalAnnualPercentageSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    if (!spouseEmployerAccount || !spouseEmployerAccount.planType) return 0

    switch (spouseEmployerAccount.planType) {
      case 2:
        return this.totalAnnualPercentageSpouse403b
      case 4:
        return this.totalAnnualPercentageSpouse457
      default:
        return this.totalAnnualPercentageSpouse401k401a
    }
  },
  get totalAnnualPercentageSpouse401k401a() {
    return (
      (self?.spouse?.preTaxAnnualContributions ?? 0) +
      (self?.spouse?.postTaxAnnualContributions ?? 0) +
      (self?.spouse?.rothAnnualContributions ?? 0)
    )
  },
  get totalAnnualPercentageSpouse403b() {
    return self?.spouse?.preTax403bAnnualContributions ?? 0
  },
  get totalAnnualPercentageSpouse457() {
    return (
      (self?.spouse?.preTax457AnnualContributions ?? 0) +
      (self?.spouse?.roth457AnnualContributions ?? 0)
    )
  },
  get client457PreTaxSaving() {
    return (self.primary || 0) && self.primary.preTax457AnnualContributions
  },
  get client457RothSaving() {
    return (self.primary || 0) && self.primary.roth457AnnualContributions
  },
  get client457PreTaxAmount() {
    const { annualSalary } = getRoot(self).account
    return (self.client457PreTaxSaving / 100) * annualSalary
  },
  get client457RothAmount() {
    const { annualSalary } = getRoot(self).account
    return (self.client457RothSaving / 100) * annualSalary
  },
  get total457Savings() {
    return self.client457PreTaxSaving + self.client457RothSaving
  },
  get total457Amount() {
    const { annualSalary } = getRoot(self).account
    return (self.total457Savings / 100) * annualSalary
  },
  get client403bPreTaxSaving() {
    return (self.primary || 0) && self.primary.preTax403bAnnualContributions
  },
  get client403bPreTaxAmount() {
    const { annualSalary } = getRoot(self).account
    return (self.client403bPreTaxSaving / 100) * annualSalary
  },
  get total403bSavings() {
    return (self.primary || 0) && self.primary.preTax403bAnnualContributions
  },
  get total403bAmount() {
    const { annualSalary } = getRoot(self).account
    return (self.total403bSavings / 100) * annualSalary
  },
  get estimatedTotalSSIIncome() {
    if (self.primary && self.spouse) {
      return self.primary.preTaxSSIncome + self.spouse.preTaxSSIncome
    } else if (self.primary && self.spouse === null) {
      return self.primary.preTaxSSIncome
    } else if (self.primary === null && self.spouse) {
      return self.spouse.preTaxSSIncome
    }
    return 0
  },
  get goalNameList() {
    const goalList = []
    for (let i = 0; i < self.includedGoals.length; i++) {
      goalList.push(self.includedGoals[i].name)
    }
    return goalList.join(', ')
  },
  get investmentMixHelpData() {
    const difference =
      self.primary && self.primary.retAge
        ? self.primary.retAge - getRoot(self).person.age
        : 65 - getRoot(self).person.age

    if (difference >= 43) {
      return investmentMixTable[43]
    } else if (difference >= 29) {
      return investmentMixTable[29]
    } else if (difference >= 20) {
      return investmentMixTable[20]
    } else if (difference >= 10) {
      return investmentMixTable[10]
    } else if (difference >= 7) {
      return investmentMixTable[7]
    } else if (difference >= 3) {
      return investmentMixTable[3]
    } else if (difference >= 1) {
      return investmentMixTable[1]
    } else {
      return investmentMixTable[0]
    }
  },
  get companyStockNames() {
    if (!self.companyStocks) {
      return ''
    }

    const stockNames = self.companyStocks.map(({ securityName }) => securityName)

    if (stockNames.length === 1) {
      return stockNames[0]
    }

    if (stockNames.length === 2) {
      return stockNames.join(' and ')
    }

    if (stockNames.length > 2) {
      const lastName = 'and ' + stockNames.pop()
      stockNames.push(lastName)
    }

    return stockNames.join(', ')
  },
  get companyStocksToKeep() {
    return self.companyStocks.filter(companyStock => companyStock.percentToSell !== 100)
  },
  get companyStockNamesToKeep() {
    if (!self.companyStocks) {
      return ''
    }

    const stockNames = self.companyStocksToKeep.map(({ securityName }) => securityName)

    if (stockNames.length === 1) {
      return stockNames[0]
    }

    if (stockNames.length === 2) {
      return stockNames.join(' and ')
    }

    if (stockNames.length > 2) {
      const lastName = 'and ' + stockNames.pop()
      stockNames.push(lastName)
    }

    return stockNames.join(', ')
  },
  get companyStockKeepPercentage() {
    if (!self.companyStocks) {
      return 0
    }

    return self.companyStocksToKeep.reduce(
      (percentage, stock) => percentage + (100 - stock.percentToSell),
      0
    )
  },
  get companyStockKeepAmount() {
    return self.companyStocksToKeep.reduce(
      (amount, stock) => amount + (stock.totalValue - stock.amountToSell),
      0
    )
  },
  get companyStockSellPercentage() {
    if (!self.companyStocks) {
      return 0
    }

    return self.companyStocks.reduce((percentage, stock) => percentage + stock.percentToSell, 0)
  },
  get unrestrictedCompanyStocks() {
    if (!self.companyStocks) {
      return []
    }

    return self.companyStocks.filter(companyStock => !companyStock.restricted)
  },
  get restrictedCompanyStocks() {
    if (!self.companyStocks) {
      return []
    }

    return self.companyStocks.filter(companyStock => companyStock.restricted)
  },
  get includedStockOptions() {
    if (!self.stockOptions) {
      return []
    }

    return self.stockOptions.filter(stockOption => stockOption.included)
  },
  get preTaxAnnualContributions() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 2:
        return self?.primary?.preTax403bAnnualContributions ?? 0
      case 4:
        return self?.primary?.preTax457AnnualContributions ?? 0
      case 1:
      case 23:
      default:
        return self?.primary?.preTaxAnnualContributions ?? 0
    }
  },

  get preTaxAnnualContribDollars() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 2:
        return self?.primary?.preTax403bAnnualContribDollars ?? 0
      case 4:
        return self?.primary?.preTax457AnnualContribDollars ?? 0
      case 1:
      case 23:
      default:
        return self?.primary?.preTaxAnnualContribDollars ?? 0
    }
  },

  get rothAnnualContributions() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 4:
        return self?.primary?.roth457AnnualContributions ?? 0
      case 1:
      case 23:
      default:
        return self?.primary?.rothAnnualContributions ?? 0
    }
  },
  get rothAnnualContribDollars() {
    const { employerAccount } = getRoot(self)

    if (!employerAccount[0] || !employerAccount[0].planType) return 0

    switch (employerAccount[0].planType) {
      case 4:
        return self?.primary?.roth457AnnualContribDollars ?? 0
      case 1:
      case 23:
      default:
        return self?.primary?.rothAnnualContribDollars ?? 0
    }
  },
  get preTaxAnnualContributionsSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    switch (spouseEmployerAccount?.planType) {
      case 2:
        return self?.spouse?.preTax403bAnnualContributions ?? 0
      case 4:
        return self?.spouse?.preTax457AnnualContributions ?? 0
      case 1:
      case 23:
      default:
        return self?.spouse?.preTaxAnnualContributions ?? 0
    }
  },

  get preTaxAnnualContribDollarsSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    switch (spouseEmployerAccount?.planType) {
      case 2:
        return self?.spouse?.preTax403bAnnualContribDollars ?? 0
      case 4:
        return self?.spouse?.preTax457AnnualContribDollars ?? 0
      case 1:
      case 23:
      default:
        return self?.spouse?.preTaxAnnualContribDollars ?? 0
    }
  },

  get rothAnnualContributionsSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    switch (spouseEmployerAccount?.planType) {
      case 4:
        return self?.spouse?.roth457AnnualContributions ?? 0
      case 1:
      case 23:
      default:
        return self?.spouse?.rothAnnualContributions ?? 0
    }
  },
  get rothAnnualContribDollarsSpouse() {
    const { spouseEmployerAccount } = getRoot(self)

    switch (spouseEmployerAccount?.planType) {
      case 4:
        return self?.spouse?.roth457AnnualContribDollars ?? 0
      case 1:
      case 23:
      default:
        return self?.spouse?.rothAnnualContribDollars ?? 0
    }
  },
}))

const Contribution = model({ taxType: number, deferralType: string, value: number })

const ContributionType = model({
  1: maybe(Contribution),
  2: maybe(Contribution),
  5: maybe(Contribution),
})

const ContributionDistribution = model({
  savingsRate: maybe(ContributionType),
  catchUp: maybe(ContributionType),
})

const employerConfig = model({
  rothContribAllowed: maybeNull(boolean),
  posttaxContribAllowed: maybeNull(boolean),
  rateChangeAllowed: false,
  balance: 0,
})

const getPreTaxContributions = (user, planType) => {
  switch (planType) {
    case 2:
      return user?.preTax403bAnnualContributions ?? 0
    case 4:
      return user?.preTax457AnnualContributions ?? 0
    case 1:
    case 23:
    default:
      return user?.preTaxAnnualContributions ?? 0
  }
}

const getRothContributions = (user, planType) => {
  switch (planType) {
    case 4:
      return user?.roth457AnnualContributions ?? 0
    case 1:
    case 23:
    default:
      return user?.rothAnnualContributions ?? 0
  }
}

const EngineStore = model({
  baseCase: maybeNull(EngineCase),
  recommendedCase: maybeNull(EngineCase),
  modifiedCase: maybeNull(EngineCase),
  acceptedCase: maybeNull(EngineCase),
  savedScenarios: array(EngineCase),
  acceptedCaseId: maybeNull(number),

  // Stores the accepted case savings rate and catch-up contributions
  acceptedContributionDistribution: maybeNull(ContributionDistribution),

  // Institutional account
  primaryEmployerConfig: optional(employerConfig, {}),

  // Spouse Employer Plan
  spouseEmployerConfig: optional(employerConfig, {}),
})
  .views(self => ({
    get clientContributionChange() {
      const { employerAccount } = getRoot(self)

      if (
        self.baseCase.primary !== null &&
        self.acceptedCase.primary !== null &&
        employerAccount?.length > 0
      ) {
        const baseCase = self.baseCase.primary
        const acceptedCase = self.acceptedCase.primary
        const planType = employerAccount[0].planType

        const baseCasePreTax = getPreTaxContributions(baseCase, planType)
        const baseCaseRoth = getRothContributions(baseCase, planType)
        const baseCasePostTax = self.baseCase.primary.postTaxAnnualContributions

        const acceptedCasePreTax = getPreTaxContributions(acceptedCase, planType)
        const acceptedCaseRoth = getRothContributions(acceptedCase, planType)
        const acceptedCasePostTax = self.acceptedCase.primary.postTaxAnnualContributions

        return (
          baseCasePreTax !== acceptedCasePreTax ||
          baseCasePostTax !== acceptedCasePostTax ||
          baseCaseRoth !== acceptedCaseRoth
        )
      }
      return false
    },
    get spouseContributionChange() {
      const { spouseEmployerAccount } = getRoot(self)
      if (
        self.baseCase.spouse !== null &&
        self.acceptedCase.spouse !== null &&
        Boolean(spouseEmployerAccount)
      ) {
        const baseCase = self.baseCase.spouse
        const acceptedCase = self.acceptedCase.spouse
        const planType = spouseEmployerAccount?.planType

        const baseCasePreTax = getPreTaxContributions(baseCase, planType)
        const baseCaseRoth = getRothContributions(baseCase, planType)
        const baseCasePostTax = self.baseCase.spouse.postTaxAnnualContributions

        const acceptedCasePreTax = getPreTaxContributions(acceptedCase, planType)
        const acceptedCaseRoth = getRothContributions(acceptedCase, planType)
        const acceptedCasePostTax = self.acceptedCase.spouse.postTaxAnnualContributions

        return (
          baseCasePreTax !== acceptedCasePreTax ||
          baseCasePostTax !== acceptedCasePostTax ||
          baseCaseRoth !== acceptedCaseRoth
        )
      }
      return false
    },
  }))
  .actions(self => ({
    getBaseCase: flow(function* () {
      const baseCase = yield API.post('engine/base')
      self.baseCase = baseCase.data
    }),
    getRecommendedCase: flow(function* () {
      const recommendedCase = yield API.post('engine/recommended')
      self.recommendedCase = recommendedCase.data
    }),
    getModifiedCase: flow(function* (modifiedValues) {
      const modifiedCase = yield API.post('engine/modified', modifiedValues)
      self.modifiedCase = modifiedCase.data
    }),
    getAcceptedCase: flow(function* (caseId) {
      const acceptedCase = yield API.get('offboard/getCaseById', { params: { caseId } })
      self.acceptedCase = acceptedCase.data
    }),
    getAcceptedContributionDistribution: flow(function* (caseId) {
      const acceptedContributionDistribution = yield API.get('offboard/catchup', {
        params: { caseId },
      })
      self.setAcceptedContributionDistribution({ catchUp: acceptedContributionDistribution.data })
    }),
    getSavedScenarios: flow(function* () {
      const savedScenarios = yield API.get('engine/previousCase')
      self.savedScenarios = savedScenarios.data
    }),
    saveScenario: flow(function* ({ name, scenarioDescription }) {
      const savedScenario = yield API.post(
        'engine/save',
        {
          ...getSnapshot(self.modifiedCase),
          name,
          scenarioDescription,
        },
        { params: { name, scenarioDescription } }
      )
      self.modifiedCase = savedScenario.data
      yield self.getSavedScenarios()
    }),
    getPrimaryEmployerConfig: flow(function* () {
      const primaryEmployerAccount = yield new Promise(resolve => {
        resolve({ data: self.active })
      })

      const { rothContribAllowed, posttaxContribAllowed, rateChangeAllowed, balance } =
        primaryEmployerAccount.data

      self.primaryEmployerConfig = balance
        ? {
            rothContribAllowed,
            posttaxContribAllowed,
            rateChangeAllowed,
            balance,
          }
        : {
            rothContribAllowed: rothContribAllowed === null ? false : rothContribAllowed,
            posttaxContribAllowed: posttaxContribAllowed === null ? false : posttaxContribAllowed,
            rateChangeAllowed: rateChangeAllowed === null ? false : rateChangeAllowed,
            balance: balance === null ? 0 : balance,
          }
    }),
    getSpouseEmployerConfig: flow(function* () {
      const { spouseEmployerAccount } = self

      if (spouseEmployerAccount) {
        const spouseAccount = yield API.get(`accounts/nonGCAccount/${spouseEmployerAccount.id}`)
        const { rothContribAllowed, posttaxContribAllowed, balance } = spouseAccount.data
        self.spouseEmployerConfig = {
          rothContribAllowed,
          posttaxContribAllowed,
          balance,
        }
      } else {
        self.spouseEmployerConfig = {}
      }
    }),
    setAcceptedCaseId(value) {
      self.acceptedCaseId = value
    },
    resetModifiedCase() {
      self.modifiedCase = null
    },
    setAcceptedContributionDistribution(value) {
      self.acceptedContributionDistribution = value
    },
    updateAcceptedCase: flow(function* (contributionDistribution = null) {
      const { person, spouse, updatePerson, updateSpouse } = getRoot(self)

      yield API.post('offboard/updateAcceptedCase', contributionDistribution, {
        headers: { 'content-type': 'application/json' },
        params: { caseId: self.acceptedCaseId },
      })

      const acceptedCase = self.acceptedCase
      if (acceptedCase?.primary?.retAge && acceptedCase.primary.retAge !== person.retirementAge) {
        yield updatePerson({ id: person.id, retirementAge: acceptedCase.primary.retAge })
      }

      if (
        spouse &&
        acceptedCase?.spouse?.retAge &&
        acceptedCase.spouse.retAge !== spouse.retirementAge
      ) {
        yield updateSpouse({ id: spouse.id, retirementAge: acceptedCase.spouse.retAge })
      }
    }),
  }))

export default EngineStore
